import type { AgentsSummaryResponse } from '@kanbu/schema/contracts';
import { ShopProductStatus } from '@kanbu/schema/enums';
import { Trans, useLingui } from '@lingui/react/macro';
import { ChevronRightIcon, Loader2Icon } from 'lucide-react';

import { Surface } from '@/components/surface/surface';
import { UrlList, UrlListItem } from '@/components/url-list/url-list';
import { AppSettings } from '@/constants';
import { cn } from '@/lib';
import { formatFeedPrice } from '@/lib/format-feed-price';
import {
  Accordion,
  AccordionTrigger,
  AccordionItem,
  AccordionContent,
} from '@/ui/accordion';
import { Button } from '@/ui/button';

import { AccordionIdentifies } from './accordion-identifiers';

export interface KnownFeedsAccordionProps {
  className?: string;
  summary: AgentsSummaryResponse | undefined;
  chatId: string;
  onValueChange: (value: string[]) => void;
  value: string[];
}

export function KnownFeedsAccordion({
  summary,
  chatId,
  onValueChange,
  value,
  className,
}: KnownFeedsAccordionProps) {
  const { i18n } = useLingui();

  return (
    <Accordion
      type='multiple'
      value={value}
      onValueChange={onValueChange}
      className={className}
    >
      <AccordionItem value={AccordionIdentifies.Feeds}>
        <AccordionTrigger>
          <div className='flex flex-col gap-1'>
            <div className='flex flex-row gap-1 items-baseline'>
              <Trans>Product Feeds</Trans>
            </div>
          </div>
        </AccordionTrigger>
        <AccordionContent>
          {summary?.feeds?.length ? (
            <div className='flex flex-col gap-2'>
              {summary?.feeds?.map(feed => {
                const doneCount = feed.productStatusCounters.done;
                const totalCount =
                  doneCount +
                  feed.productStatusCounters.pending +
                  feed.productStatusCounters.embedding +
                  feed.productStatusCounters.failed;

                return (
                  <Surface key={feed.id} className='bg-background/0'>
                    <h4 className='text-base font-medium mb-2'>
                      {feed.name}{' '}
                      <span className='text-muted-foreground text-xs'>
                        ({doneCount}/{totalCount}{' '}
                        <Trans>imported products</Trans>)
                      </span>
                    </h4>
                    <div className='text-sm'>
                      <UrlList
                        itemsCount={feed.products.length}
                        totalCount={feed.productsCount}
                      >
                        {feed.products.map(product => {
                          const isDone =
                            product.status === ShopProductStatus.Done;

                          return (
                            <UrlListItem
                              key={product.id}
                              url={product.url}
                              className='bg-background/75'
                              linkClassName={cn(
                                !isDone && 'pr-4 text-muted-foreground',
                              )}
                            >
                              {product.name} -{' '}
                              <span className='font-medium'>
                                {product.price == null ? (
                                  <Trans>Price not available</Trans>
                                ) : (
                                  formatFeedPrice({
                                    price: product.price,
                                    currency: product.currency,
                                    locale: i18n.locale,
                                  })
                                )}
                              </span>
                              {!isDone && (
                                <span className='absolute right-0 top-0'>
                                  <Loader2Icon
                                    className='size-2.5 text-primary animate-spin'
                                    strokeWidth={2.5}
                                  />
                                </span>
                              )}
                            </UrlListItem>
                          );
                        })}
                      </UrlList>
                    </div>
                  </Surface>
                );
              })}
            </div>
          ) : (
            <p>
              <Trans>
                Your chatbot does not have any product feeds trained yet, use
                the button below to add new ones.
              </Trans>
            </p>
          )}
          <div className='mt-2'>
            <Button
              data-cta='playground-agent-info-section-add-sources-admin-button'
              variant='outline'
              size='sm'
              asChild
            >
              <a
                href={`${AppSettings.adminAppUrl}/chats/${chatId}/feeds`}
                target='_blank'
                rel='noreferrer'
              >
                <Trans>Add feeds</Trans> <ChevronRightIcon className='size-4' />
              </a>
            </Button>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
